import { t } from 'ttag'
import { useMutation, useQuery } from 'react-query'
import { useCallback, useContext, useState } from 'react'
import styled from 'styled-components'

import { Text } from '@h2/components/typography/text'
import { api, errors } from '@h2/api'
import { Button } from '@h2/components/buttons/button'
import { Globals } from '@h2/globals'
import { Page } from '@h2/common/components/page'
import { PB70T, SG90T, PB97T, PB80T, PB40T } from '@h2/components/colors'
import { Skeleton } from '@h2/components/loading/skeleton'
import { useForm } from '@h2/common/components/hooks/useForm'
import { Field } from '@h2/components/inputs/field'
import { Flex } from '@h2/components/layout/flex'
import { useNavigator } from '@h2/common/components/hooks/useNavigator'

import { ZipCodeForm } from './components/zipCodeForm'

const CountySelector = styled.div`
  border: 1px solid ${PB70T};
  background-color: ${SG90T};
  padding: 15px;
  margin-bottom: 25px;
`
const StateSelector = styled.div`
  border: 1px solid ${PB70T};
  background-color: ${PB97T};
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 25px;
`
const Select = styled.select`
  margin: 0;
  box-sizing: border-box;
  outline: 0;
  border: 1px solid ${PB80T};
  border-radius: 5px;
  color: ${PB40T};
  font-size: 14px;
  padding: 8px 14px;
  line-height: 1;
`
const BudgetCounty = ({ budget, updateBudget }) => {
  const { budgetTitle } = useContext(Globals)
  const navigator = useNavigator()
  const [countyClicked, setCountyClicked] = useState(null)
  const [selectedState, setSelectedState] = useState(budget?.stateAbbreviation)
  const { data: locations, isFetching } = useQuery(
    ['budget-county-listing', selectedState],
    () => {
      return api.fetch(`/budget/${budget?.id ?? -1}/locations`, {
        method: 'POST',
        body: JSON.stringify({ state: selectedState })
      })
    }
  )
  const { register, error, isInvalid, dispatch, handleOnSubmit } = useForm({
    initialValues: {
      state: locations?.selectedState ?? selectedState
    },
    state(form) {
      if (!form.state?.trim()) {
        return t`A valid state is required`
      }
    }
  })
  const handleForm = useCallback(
    form => {
      setSelectedState(form.state)
    },
    [setSelectedState]
  )
  const { mutate: selectCounty, isLoading: isSelecting } = useMutation(
    fipsCode => {
      setCountyClicked(fipsCode)

      return api.fetch(`/budget/${budget?.id ?? -1}/respond/location`, {
        method: 'POST',
        body: JSON.stringify({ fipsCode })
      })
    },
    {
      onSuccess: newBudget => {
        updateBudget(newBudget)
        setCountyClicked(null)
        navigator(`/budget/${newBudget.id}/question/0`)
      },
      onError: err => {
        const formErrors = errors.getValidation(err)

        if (formErrors) {
          dispatch({ type: 'validate', payload: formErrors })
        }

        setCountyClicked(null)
      }
    }
  )

  return (
    <Page>
      <Page.Title>{budgetTitle}</Page.Title>
      <Page.Layout>
        <Page.Content>
          <CountySelector>
            <ZipCodeForm budgetId={budget?.id ?? '-1'} updateBudget={updateBudget} />
          </CountySelector>
          <Text size="large">{t`Or, select the county you would like to live in by clicking the name in list below.`}</Text>
          {isFetching ? (
            <Skeleton height="100px" />
          ) : (
            <>
              <StateSelector>
                <form onSubmit={handleOnSubmit(form => handleForm(form))}>
                  <Flex gap="10px" alignItems="center">
                    <Field
                      isRequired
                      label={t`State`}
                      desc={error.state}
                      status={error.state ? 'error' : 'default'}>
                      <Select {...register('state')}>
                        <option value="US">{t`Please choose one`}</option>
                        {locations?.states.map(state => (
                          <option key={state.id} value={state.id}>
                            {state.name}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <div>
                      <Button type="submit" isDisabled={isInvalid}>
                        {t`Select`}
                      </Button>
                    </div>
                  </Flex>
                </form>
              </StateSelector>

              <ul id="counties" style={{ columnCount: '4' }}>
                {locations?.counties.map(county => (
                  <li key={county.fipsCode}>
                    <Button
                      variant="link"
                      isLoading={isSelecting && countyClicked === county.fipsCode}
                      isDisabled={isSelecting}
                      onClick={() => selectCounty(county.fipsCode)}>
                      {county.name}
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Page.Content>
      </Page.Layout>
    </Page>
  )
}

export { BudgetCounty }
